import { useState, type ReactNode } from "react";
import { Link as RouterLink } from "react-router-dom";
import Alert, { type AlertProps } from "@mui/material/Alert";
import AlertTitle from "@mui/material/AlertTitle";
import Button from "@mui/material/Button";
import Stack from "@mui/material/Stack";
import {
  GUARDIAN_CONTROL_ERROR_CODES,
  isApiError,
  type GuardianDenial,
} from "@bw/shared/api";
import {
  useCreateGuardianRequest,
  useMyGuardianControls,
} from "../query/useGuardianLink";
import { guardianControlLabel } from "./controls";

/**
 * The shared answer to a guardian-control 403 (#2691), used by the app-wide
 * prompt and inline (a mutation with its own error area, a dialog):
 *
 *   - GUARDIAN_REQUIRED          → look-only: ask a parent or guardian to approve
 *                                  the account (or: a guardian paused it);
 *   - GUARDIAN_DECLINED          → "A guardian has turned this off";
 *   - GUARDIAN_APPROVAL_REQUIRED → "Ask a guardian" creates the request, then
 *                                  "Waiting for a guardian";
 *   - GUARDIAN_MEMBER_APPROVAL_REQUIRED → someone else this adds needs their
 *                                  own guardian's approval (nothing to ask).
 */
export function GuardianDenialNotice({
  denial,
  onClose,
}: {
  denial: GuardianDenial;
  onClose?: () => void;
}) {
  const controls = useMyGuardianControls(
    denial.code === GUARDIAN_CONTROL_ERROR_CODES.REQUIRED,
  );
  const ask = useCreateGuardianRequest();
  const [askedId, setAskedId] = useState<string | null>(null);
  const activity = guardianControlLabel(denial.control);
  const close = onClose ? (
    <Button color="inherit" size="small" onClick={onClose}>
      Close
    </Button>
  ) : null;

  if (denial.code === GUARDIAN_CONTROL_ERROR_CODES.MEMBER_APPROVAL_REQUIRED) {
    return (
      <Notice title="Someone here needs a guardian's approval" actions={close}>
        A player this includes needs their own guardian to allow it first. Their
        guardian can do that from their Family page.
      </Notice>
    );
  }

  if (denial.code === GUARDIAN_CONTROL_ERROR_CODES.REQUIRED) {
    // Until the controls load, don't guess between "waiting" and "paused".
    if (controls.isPending) {
      return (
        <Notice title="This waits for a guardian" actions={close}>
          You can look around, but this needs a guardian first.
        </Notice>
      );
    }
    const paused = controls.data?.reason === "paused";
    return (
      <Notice
        title={
          paused
            ? "A guardian has paused your account"
            : "Ask a parent or guardian to approve your account"
        }
        actions={
          paused && !close ? null : (
            <>
              {paused ? null : (
                <Button
                  component={RouterLink}
                  to="/me/guardian"
                  color="inherit"
                  size="small"
                  onClick={onClose}
                >
                  Ask a guardian
                </Button>
              )}
              {close}
            </>
          )
        }
      >
        {paused
          ? "You can look around, but this waits until a guardian turns your account back on."
          : "You can look around, but this waits until a parent or guardian approves your account."}
      </Notice>
    );
  }

  if (denial.code === GUARDIAN_CONTROL_ERROR_CODES.DECLINED) {
    return (
      <Notice title="A guardian has turned this off" actions={close}>
        {activity
          ? `${activity} isn't available on your account. A guardian can change this from their Family page.`
          : "A guardian can change this from their Family page."}
      </Notice>
    );
  }

  const waitingId = askedId ?? denial.requestId;
  if (waitingId) {
    return (
      <Notice
        title="Waiting for a guardian"
        actions={close}
        severity="success"
        role="status"
      >
        We let your guardians know. You can go ahead once one of them approves.
      </Notice>
    );
  }

  if (!denial.subject || !denial.control) {
    return (
      <Notice title="A guardian needs to allow this first" actions={close}>
        {activity
          ? `Ask a guardian to allow ${activity.toLowerCase()} from their Family page.`
          : "Ask a guardian to allow this from their Family page."}
      </Notice>
    );
  }

  const subject = denial.subject;
  const control = denial.control;
  const failed = ask.isError;
  const alreadyAllowed =
    isApiError(ask.error) &&
    ask.error.code === GUARDIAN_CONTROL_ERROR_CODES.REQUEST_NOT_NEEDED;
  return (
    <Notice
      title="A guardian needs to approve this first"
      actions={
        <>
          <Button
            color="inherit"
            size="small"
            disabled={ask.isPending || alreadyAllowed}
            onClick={() =>
              ask.mutate(
                { control, subject_kind: subject.kind, subject_id: subject.id },
                { onSuccess: (request) => setAskedId(request.request_id) },
              )
            }
          >
            {ask.isPending ? "Asking…" : "Ask a guardian"}
          </Button>
          {close}
        </>
      }
    >
      {alreadyAllowed
        ? "A guardian already allowed this. Try again."
        : failed
          ? "We couldn't send your request. Please try again."
          : activity
            ? `${activity}: a guardian approves each new person or place once.`
            : "A guardian approves each new person or place once."}
    </Notice>
  );
}

/**
 * One layout for every variant: on a phone the buttons stack under the text
 * (an Alert's side action column would squeeze the message); from `sm` up
 * they sit beside it.
 */
function Notice({
  title,
  actions,
  children,
  severity = "info",
  role,
}: {
  title: string;
  actions: ReactNode;
  children: ReactNode;
  severity?: AlertProps["severity"];
  role?: string;
}) {
  return (
    <Alert
      severity={severity}
      variant="outlined"
      role={role}
      sx={{
        bgcolor: "background.paper",
        flexWrap: { xs: "wrap", sm: "nowrap" },
        "& .MuiAlert-message": {
          minWidth: 0,
          flex: { xs: "1 1 calc(100% - 40px)", sm: "1 1 auto" },
        },
        "& .MuiAlert-action": {
          ml: { xs: 0, sm: "auto" },
          pl: { xs: 0, sm: 2 },
          width: { xs: "100%", sm: "auto" },
          flexShrink: 0,
        },
        "& .MuiAlert-action .MuiButton-root": { whiteSpace: "nowrap" },
      }}
      action={
        actions ? (
          <Stack
            direction="row"
            spacing={0.5}
            useFlexGap
            sx={{
              flexWrap: { xs: "wrap", sm: "nowrap" },
              justifyContent: { xs: "flex-end", sm: "flex-start" },
              width: "100%",
            }}
          >
            {actions}
          </Stack>
        ) : undefined
      }
    >
      <AlertTitle>{title}</AlertTitle>
      {children}
    </Alert>
  );
}
